import React from 'react';
import { List, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ModalFooter } from '~/components/Modal';
import { RedTimes, TableView } from '~/stylizations/styles';

export default function CustomerView({ visible, onClose, customer, isMobile }) {
  const { t } = useTranslation();

  const label = (text) => <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{t(text)}</span>;
  const mobileLabel = (text) => (
    <div>
      <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{t(text)}</span>
    </div>
  );
  const child = (text) => {
    if (text == null || text == '') {
      return (
        <div style={{ textAlign: 'center' }}>
          <RedTimes />
        </div>
      );
    }

    return <span style={{ fontSize: isMobile ? '12px' : '15px', fontWeight: 'bold' }}>{t(`${text}`)}</span>;
  };

  const customerFields = [
    { label: 'Nome', value: customer?.name },
    {
      label: !customer?.customer_type ? 'Apelido' : 'Razão social',
      value: !customer?.customer_type ? customer?.nickname : customer?.social_name,
    },
    {
      label: !customer?.customer_type ? 'CPF' : 'CNPJ',
      value: !customer?.customer_type ? customer?.cpf : customer?.cnpj,
    },
    {
      label: !customer?.customer_type ? 'RG' : 'Inscrição estadual',
      value: !customer?.customer_type ? customer?.rg : customer?.state_registration,
    },
    { label: 'Telefone', value: customer?.phone },
    { label: 'Descrição', value: customer?.description },
    {
      label: 'Serviços',
      value: customer?.services?.map((s) => ' ' + s?.name).join(', '),
    },
    { label: 'Responsável', value: customer?.responsible_name },
    { label: 'Tipo', value: customer?.prospect_type },
    { label: 'Email', value: customer?.email },
    { label: 'Página web', value: customer?.web_page },
    { label: 'CEP', value: customer?.address?.zipcode },
    { label: 'Estado', value: customer?.city?.states?.name },
    { label: 'Cidade', value: customer?.city?.name },
    { label: 'Bairro', value: customer?.address?.neighborhood },
    { label: 'Logradouro', value: customer?.address?.street },
    { label: 'Número', value: customer?.address?.number },
    { label: 'Referência', value: customer?.address?.reference },
    { label: 'Nota', value: customer?.note },
  ];

  const contactsFields = (customer?.contacts?.length ? customer.contacts : [{}]).flatMap((contact, i) => [
    { label: `Contato ${i + 1} - Nome`, value: contact?.name },
    { label: `Contato ${i + 1} - Cargo`, value: contact?.role },
    { label: `Contato ${i + 1} - Rede social`, value: contact?.social_media },
    { label: `Contato ${i + 1} - Telefone`, value: contact?.phone },
    { label: `Contato ${i + 1} - E-mail`, value: contact?.email },
  ]);

  const data = [...customerFields, ...contactsFields];

  return (
    <Modal
      title={`${t('Informações')} - ${customer?.name ?? ''}`}
      open={visible}
      onCancel={onClose}
      centered
      width={isMobile ? '100%' : '1200px'}
      footer={<ModalFooter showOk={false} onCancel={onClose} />}
    >
      {isMobile ? (
        <List
          bordered
          dataSource={data}
          renderItem={({ label, value }) => (
            <List.Item>
              <Typography.Text strong>{mobileLabel(label)}</Typography.Text>
              {child(value)}
            </List.Item>
          )}
        />
      ) : (
        <>
          <TableView
            pagination={false}
            rowKey={() => 'basic-1'}
            bordered
            columns={[
              { title: label('Nome'), dataIndex: 'name', render: child },
              {
                title: !customer?.customer_type ? label('Apelido') : label('Razão social'),
                dataIndex: !customer?.customer_type ? 'nickname' : 'social_name',
                render: child,
              },
            ]}
            dataSource={[customer]}
          />
          <TableView
            pagination={false}
            rowKey={() => 'basic-1'}
            bordered
            columns={[
              {
                title: !customer?.customer_type ? label('CPF') : label('CNPJ'),
                dataIndex: !customer?.customer_type ? 'cpf' : 'cnpj',
                render: child,
              },
              {
                title: !customer?.customer_type ? label('RG') : label('Inscrição estadual'),
                dataIndex: !customer?.customer_type ? 'rg' : 'state_registration',
                render: child,
              },
              { title: label('Telefone'), dataIndex: 'phone', render: child },
            ]}
            dataSource={[customer]}
          />
          <TableView
            pagination={false}
            rowKey={() => 'basic-1'}
            bordered
            columns={[
              { title: label('Descrição'), dataIndex: 'description', render: child },
              {
                title: label('Serviços'),
                dataIndex: 'services',
                render: (_, record) => child(record.services.map((service, _) => ' ' + service?.name)),
              },
              { title: label('Responsável'), dataIndex: 'responsible_name', render: child },
            ]}
            dataSource={[customer]}
          />
          <TableView
            pagination={false}
            rowKey={() => 'basic-1'}
            bordered
            columns={[
              { title: label('Tipo'), dataIndex: 'prospect_type', render: child },
              { title: label('Email'), dataIndex: 'email', render: child },
              { title: label('Página web'), dataIndex: 'web_page', render: child },
            ]}
            dataSource={[customer]}
          />
          <TableView
            pagination={false}
            rowKey={() => 'basic-1'}
            bordered
            columns={[
              {
                title: label('CEP'),
                dataIndex: 'address.zipcode',
                render: (_, record) => child(record.address?.zipcode),
              },
              {
                title: label('Estado'),
                dataIndex: 'city.state.name',
                render: (_, record) => child(record.city?.states?.name),
              },
              { title: label('Cidade'), dataIndex: 'city.name', render: (_, record) => child(record.city?.name) },
            ]}
            dataSource={[customer]}
          />
          <TableView
            pagination={false}
            rowKey={() => 'basic-1'}
            bordered
            columns={[
              {
                title: label('Bairro'),
                dataIndex: 'address.neighborhood',
                render: (_, record) => child(record.address?.neighborhood),
              },
              {
                title: label('Logradouro'),
                dataIndex: 'address.street',
                render: (_, record) => child(record.address?.street),
              },
              {
                title: label('Número'),
                dataIndex: 'address.number',
                render: (_, record) => child(record.address?.number),
              },
              {
                title: label('Referência'),
                dataIndex: 'address.reference',
                render: (_, record) => child(record.address?.reference),
              },
            ]}
            dataSource={[customer]}
          />
          <TableView
            pagination={false}
            rowKey={() => 'basic-1'}
            bordered
            columns={[{ title: label('Nota'), dataIndex: 'note', render: child }]}
            dataSource={[customer]}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '20px 0px 10px 0px' }}>
            <Typography.Title style={{ fontSize: '28px' }}>Contatos</Typography.Title>
          </div>
          <TableView
            pagination={false}
            rowKey={() => 'basic-1'}
            bordered
            columns={[
              { title: label('Nome'), dataIndex: 'name', render: child },
              { title: label('Cargo'), dataIndex: 'role', render: child },
              { title: label('Rede social'), dataIndex: 'social_media', render: child },
              { title: label('Telefone'), dataIndex: 'phone', render: child },
              { title: label('E-mail'), dataIndex: 'email', render: child },
            ]}
            dataSource={customer?.contacts?.length ? customer.contacts : [{}]}
          />
        </>
      )}
    </Modal>
  );
}
